import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, ChevronLeft, Scale, AlertTriangle, Shield, Users, Ban, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function TermsOfServicePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      title: "Acceptance of Terms",
      icon: CheckCircle,
      content: `By accessing or using LocalBest.ai ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.

These terms apply to all visitors, users, business owners, and others who access or use the Service.`
    },
    {
      title: "Description of Service",
      icon: FileText,
      content: `LocalBest.ai is a business directory platform that:
      
• Provides AI-powered search and discovery of businesses
• Offers AI chat assistants for business inquiries
• Hosts business listings and profiles
• Facilitates connections between users and businesses
• Provides marketplace listings for various categories
• Features AI certification and verification services`
    },
    {
      title: "User Responsibilities",
      icon: Users,
      content: `As a user of our Service, you agree to:

• Provide accurate and complete information
• Maintain the security of your account credentials
• Not misuse or abuse the platform or AI features
• Respect the intellectual property rights of others
• Not engage in fraudulent or deceptive practices
• Comply with all applicable laws and regulations
• Not attempt to reverse engineer or exploit our AI systems`
    },
    {
      title: "Business Listings",
      icon: Shield,
      content: `Business owners who submit listings agree to:

• Provide truthful and accurate business information
• Keep their listing information current and updated
• Respond appropriately to customer inquiries
• Not make false claims about AI certification status
• Comply with our content guidelines and policies
• Accept responsibility for their business practices
• Authorize us to display their information publicly`
    },
    {
      title: "AI Technology Disclaimer",
      icon: Scale,
      content: `Our AI-powered features:

• Are provided "as is" without warranty
• May occasionally produce inaccurate responses
• Should not be relied upon for critical decisions
• Are designed to assist, not replace, human judgment
• May be improved or modified without notice
• Collect interaction data for improvement purposes
• Are subject to our AI User Policy guidelines`
    },
    {
      title: "Prohibited Activities",
      icon: Ban,
      content: `Users are prohibited from:

• Submitting false, misleading, or spam content
• Harassing, threatening, or abusing other users
• Attempting to manipulate search rankings
• Scraping or harvesting data without permission
• Using the service for illegal activities
• Impersonating businesses or individuals
• Circumventing security measures or access controls
• Distributing malware or malicious code`
    },
    {
      title: "Limitation of Liability",
      icon: AlertTriangle,
      content: `To the fullest extent permitted by law:

• We are not liable for any indirect, incidental, or consequential damages
• We do not guarantee the accuracy of business listings
• We are not responsible for transactions between users and businesses
• We do not endorse or verify all listed businesses
• Our liability is limited to the amount you paid for our services
• We are not liable for AI-generated content accuracy
• Use of the Service is at your own risk`
    }
  ];

  return (
    <div className="page-container min-h-screen">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#00E5FF]/20 via-transparent to-[#7C3AED]/10" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 relative">
          <Link to="/" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-6 transition-colors">
            <ChevronLeft className="w-4 h-4" />
            Back to Directory
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#7C3AED] flex items-center justify-center">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white">Terms of Service</h1>
                <p className="text-[#94A3B8]">Last updated: February 6, 2026</p>
              </div>
            </div>
            
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              Please read these Terms of Service carefully before using LocalBest.ai.
              By using our service, you agree to be bound by these terms.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="space-y-8">
          {sections.map((section, idx) => {
            const Icon = section.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card rounded-xl p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/20 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#00E5FF]" />
                  </div>
                  <h2 className="text-xl font-bold text-white">{section.title}</h2>
                </div>
                <div className="text-[#94A3B8] whitespace-pre-line leading-relaxed">
                  {section.content}
                </div>
              </motion.div>
            );
          })}

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="glass-card rounded-xl p-6 border-2 border-[#00E5FF]/30"
          >
            <h2 className="text-xl font-bold text-white mb-4">Questions About These Terms?</h2>
            <p className="text-[#94A3B8] mb-4">
              If you have any questions about these Terms of Service, please contact our legal team.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="mailto:legal@localbest.ai">
                <Button className="bg-gradient-to-r from-[#00E5FF] to-[#7C3AED] hover:opacity-90">
                  Contact Legal Team
                </Button>
              </a>
              <Link to="/ai-policy">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  View AI User Policy
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
